"use client";
import { useSidebar } from "@/stores/sidebar.atoms";
import type { MagicAAConnectorAdditions } from "@/util/magic-connector-aa";
import { type MagicConnectorAdditions, hasFunctionProperty } from "@/util/magic-connectors";
import { trpc } from "@/util/trpc";
import { getChain, getErrorMessage } from "@talentir/shared/helpers";
import { useQueryClient } from "@tanstack/react-query";
import type { Route } from "next";
import { useRouter } from "next/navigation";
import { usePostHog } from "posthog-js/react";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { getAddress, toHex } from "viem";
import { createSiweMessage } from "viem/siwe";
import { type Connector, useConnect, useDisconnect, useSignMessage } from "wagmi";

type AuthLoadingState =
    | {
          status: "idle";
      }
    | {
          status: "connecting";
          walletID: string;
      }
    | {
          status: "otpEntering";
          isRetry: boolean;
          email: string;
          onOtpTokenEntered: (token: string) => void;
      }
    | {
          status: "waitingForWalletToConnect";
          walletID: string;
      }
    | {
          status: "requestMessageToSign";
          walletID: string;
      }
    | {
          status: "waitForMessageToBeSigned";
          walletID: string;
      }
    | {
          status: "verifySignature";
          walletID: string;
      }
    | {
          status: "loadProfile";
          walletID: string;
      };

export type AuthLoadingStateWithProgress = AuthLoadingState & {
    progress: number;
};

type AuthContextType = {
    loadingState: AuthLoadingStateWithProgress;
    signIn: (params: { connectorIndex: number; relativeRedirectUrl?: string; email?: string }) => Promise<void>;
    connectCoinbaseSmartWallet: (params: { relativeRedirectUrl?: string; mode: "create" | "connect" }) => Promise<void>;
    signOut: (redirect?: boolean) => Promise<void>;
    reset: () => void;
    cancelSignIn: () => void;
    setActiveNonceFetching: (active: boolean) => void;
};

const AuthContext = createContext<AuthContextType | null>(null);

function getProgressFromLoadingState(loadingState: AuthLoadingState): number {
    switch (loadingState.status) {
        case "idle":
            return 0;
        case "connecting":
            return 12.5;
        case "waitingForWalletToConnect":
            return 25;
        case "requestMessageToSign":
            return 37.5;
        case "otpEntering":
            return 50;
        case "waitForMessageToBeSigned":
            return 62.5;
        case "verifySignature":
            return 75;
        case "loadProfile":
            return 87.5;
        default:
            return 0;
    }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
    const { connectAsync, connectors, reset: resetConnect } = useConnect();
    const { disconnectAsync } = useDisconnect();
    const { signMessageAsync, reset: resetSignIn } = useSignMessage();
    const [, setSidebar] = useSidebar();

    const [loadingState, setLoadingState] = useState<AuthLoadingState>({
        status: "idle",
    });
    const [currentlyConnectingConnector, setCurrentlyConnectingConnector] = useState<Connector | null>(null);
    const router = useRouter();
    const utils = trpc.useUtils();
    const signInMutation = trpc.lambda.auth.signIn.useMutation();
    const mockSignInMutation = trpc.lambda.auth.mockSignIn.useMutation();
    const queryClient = useQueryClient();

    const [activeNonceFetch, setActiveNonceFetching] = useState(0);
    const { data: nonce } = trpc.lambda.auth.getNonce.useQuery(undefined, {
        refetchInterval: 5_000,
        enabled: activeNonceFetch > 0,
    });

    const coinbaseWalletConnector = useMemo(
        () => connectors.find((connector) => connector.id === "baseAccountSDK"),
        [connectors],
    );

    const cancelSignIn = useCallback(() => {
        setLoadingState({ status: "idle" });
        setCurrentlyConnectingConnector(null);

        if (currentlyConnectingConnector) {
            const hasCancel = hasFunctionProperty<MagicConnectorAdditions>(currentlyConnectingConnector, "cancel");
            if (hasCancel) {
                currentlyConnectingConnector.cancel();
            }
        }
    }, [currentlyConnectingConnector]);

    const connectCoinbaseSmartWallet = useCallback(
        async ({ relativeRedirectUrl }: { relativeRedirectUrl?: string }) => {
            try {
                if (coinbaseWalletConnector == null) {
                    throw new Error("Coinbase wallet connector not found");
                }
                setLoadingState({ status: "connecting", walletID: coinbaseWalletConnector.id });

                setCurrentlyConnectingConnector(coinbaseWalletConnector);

                const provider = await coinbaseWalletConnector.getProvider();

                setLoadingState({ status: "requestMessageToSign", walletID: coinbaseWalletConnector.id });

                const localNonce = nonce ?? (await utils.lambda.auth.getNonce.fetch());

                try {
                    await disconnectAsync();
                } catch {}

                setLoadingState({ status: "waitForMessageToBeSigned", walletID: coinbaseWalletConnector.id });

                // biome-ignore lint/suspicious/noExplicitAny: provider is not typed
                const { accounts } = await (provider as any).request({
                    method: "wallet_connect",
                    params: [
                        {
                            version: "1",
                            capabilities: {
                                signInWithEthereum: {
                                    nonce: localNonce,
                                    chainId: toHex(getChain().id),
                                    version: "1",
                                    issuedAt: new Date().toISOString(),
                                },
                            },
                        },
                    ],
                });

                const { address } = accounts[0];
                const { message, signature } = accounts[0].capabilities.signInWithEthereum;

                await signInInternal({
                    accountAddress: address,
                    connector: coinbaseWalletConnector,
                    relativeRedirectUrl,
                    unsignedMessage: message,
                    signedMessage: signature,
                    accountAbstractionOwner: undefined,
                    category: "coinbase-wallet",
                });
            } catch (e) {
                console.error(e);
                try {
                    await disconnectAsync();
                } catch {}
                const errorMessage = getErrorMessage(e);
                toast.error(errorMessage);
                console.log("Wallet Sign In: Error occurred, returning to idle");
                setLoadingState({ status: "idle" });
                setCurrentlyConnectingConnector(null);
            }
        },
        [coinbaseWalletConnector, disconnectAsync, nonce, utils.lambda.auth.getNonce.fetch],
    );

    const signIn = useCallback(
        async ({
            connectorIndex,
            email,
            relativeRedirectUrl,
        }: { connectorIndex: number; email?: string; relativeRedirectUrl?: string }): Promise<void> => {
            try {
                if (email === "jk+mock@talentir.com") {
                    await mockSignInMutation.mutateAsync();
                    router.push("/dashboard/instagram");
                    setSidebar({ open: false });
                    return;
                }

                let connector = connectors[connectorIndex];
                if (connector == null) {
                    throw new Error("Invalid connector");
                }
                const walletID = connector.id;

                console.log("Wallet Sign In: Connecting to wallet");
                setLoadingState({ status: "connecting", walletID });

                try {
                    await disconnectAsync();
                } catch {}

                if (connector.id === "magic" || connector.id === "magic-aa") {
                    if (email === undefined) {
                        throw new Error("Email is required for Magic connector");
                    }

                    if (await utils.lambda.auth.shouldUseAccountAbstractionWallet.fetch({ email })) {
                        connector = connectors.find((c) => c.id === "magic-aa") ?? connector;
                    } else {
                        connector = connectors.find((c) => c.id === "magic") ?? connector;
                    }
                    setCurrentlyConnectingConnector(connector);

                    if (hasFunctionProperty<MagicConnectorAdditions>(connector, "setEmail")) {
                        connector.setEmail(email);
                    }

                    if (hasFunctionProperty<MagicConnectorAdditions>(connector, "setOnOtpEnterReady")) {
                        connector.setOnOtpEnterReady((tokenEntered, isRetry) => {
                            console.log("Wallet Sign In: Waiting for OTP entry");
                            setLoadingState({
                                status: "otpEntering",
                                isRetry,
                                email,
                                onOtpTokenEntered: (value) => {
                                    console.log("Wallet Sign In: Waiting for wallet connection");
                                    setLoadingState({ status: "waitingForWalletToConnect", walletID });
                                    tokenEntered(value);
                                },
                            });
                        });
                    }
                }

                const chain = getChain();

                const { accounts } = await connectAsync({ connector, chainId: chain.id });

                console.log("Wallet Sign In: Requesting message to sign");
                setLoadingState({ status: "requestMessageToSign", walletID: connector.id });

                const localNonce = nonce ?? (await utils.lambda.auth.getNonce.fetch());

                const unsignedMessage = createSiweMessage({
                    domain: "talentir.com",
                    address: getAddress(accounts[0]),
                    statement: "Sign in with Ethereum",
                    version: "1",
                    chainId: getChain().id,
                    nonce: localNonce,
                    issuedAt: new Date(),
                    uri: "https://talentir.com",
                });

                const accountAbstractionOwner = await (async () => {
                    if (
                        connector.id === "magic-aa" &&
                        hasFunctionProperty<MagicAAConnectorAdditions>(connector, "getOwner")
                    ) {
                        return await connector.getOwner();
                    }
                })();

                console.log("Wallet Sign In: Waiting for message to be signed");
                setLoadingState({ status: "waitForMessageToBeSigned", walletID: connector.id });
                const signedMessage = await signMessageAsync({ message: unsignedMessage });

                await signInInternal({
                    accountAddress: accounts[0],
                    connector,
                    relativeRedirectUrl,
                    unsignedMessage,
                    signedMessage,
                    accountAbstractionOwner,
                    category: (() => {
                        if (connector.id === "magic") {
                            return "magic";
                        }

                        if (connector.id === "magic-aa") {
                            return "magic-with-account-abstraction";
                        }

                        return "ethereum";
                    })(),
                });
            } catch (e) {
                console.error(e);
                try {
                    await disconnectAsync();
                } catch {}
                console.log("Wallet Sign In: Error occurred, returning to idle");
                setLoadingState({ status: "idle" });
                setCurrentlyConnectingConnector(null);
            }
        },
        [
            connectAsync,
            connectors,
            disconnectAsync,
            mockSignInMutation,
            router,
            setSidebar,
            utils,
            signMessageAsync,
            nonce,
        ],
    );

    const signInInternal = useCallback(
        async ({
            accountAddress,
            connector,
            relativeRedirectUrl,
            unsignedMessage,
            signedMessage,
            accountAbstractionOwner,
            category,
        }: {
            accountAddress: string;
            connector: Connector;
            relativeRedirectUrl?: string;
            unsignedMessage: string;
            signedMessage: string;
            accountAbstractionOwner: string | undefined;
            category: "magic" | "magic-with-account-abstraction" | "coinbase-wallet" | "ethereum";
        }) => {
            console.log("Wallet Sign In: Verifying signature", signedMessage);
            setLoadingState({ status: "verifySignature", walletID: connector.id });

            const { hasChannelConnected } = await signInMutation.mutateAsync({
                ethereumAddress: accountAddress,
                signedMessage,
                category,
                accountAbstractionOwnerId: accountAbstractionOwner,
                unsignedMessage,
            });

            console.log("Wallet Sign In: Loading profile");
            setLoadingState({ status: "loadProfile", walletID: connector.id });

            await utils.invalidate();

            if (relativeRedirectUrl !== undefined) {
                router.push(relativeRedirectUrl as Route);
            } else if (!hasChannelConnected) {
                router.push("/signup/connect-youtube-channel");
            } else {
                router.push("/dashboard");
            }

            setLoadingState({ status: "idle" });
            setSidebar({ open: false });
        },
        [router, setSidebar, signInMutation, utils],
    );

    const reset = useCallback(() => {
        resetConnect();
        resetSignIn();
        setLoadingState({ status: "idle" });
    }, [resetConnect, resetSignIn]);

    const posthog = usePostHog();
    const signOutMutation = trpc.lambda.auth.signOut.useMutation();

    const signOut = useCallback(
        async (redirect = true) => {
            setLoadingState({ status: "idle" });
            await signOutMutation.mutateAsync();
            if (redirect) {
                router.push("/home");
            }

            await disconnectAsync();
            await queryClient.resetQueries();
            posthog.reset();
        },
        [disconnectAsync, router, posthog, signOutMutation, queryClient.resetQueries],
    );

    const setActiveNonceFetchingImpl = useCallback((active: boolean) => {
        setActiveNonceFetching((prev) => (active ? prev + 1 : Math.max(0, prev - 1)));
    }, []);

    const value = useMemo(
        () => ({
            loadingState: {
                ...loadingState,
                progress: getProgressFromLoadingState(loadingState),
            },
            signIn,
            connectCoinbaseSmartWallet,
            signOut,
            reset,
            cancelSignIn,
            setActiveNonceFetching: setActiveNonceFetchingImpl,
        }),
        [loadingState, signIn, connectCoinbaseSmartWallet, signOut, reset, cancelSignIn, setActiveNonceFetchingImpl],
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
}

export function usePrefetchNonce() {
    const { setActiveNonceFetching } = useAuth();

    useEffect(() => {
        setActiveNonceFetching(true);

        return () => {
            setActiveNonceFetching(false);
        };
    }, [setActiveNonceFetching]);
}
